import React, { useEffect, useMemo, useState } from 'react'
import { isEmpty, isNull } from 'lodash'
import arrayMove from 'array-move'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import SidePanel from '../../../../../ui/SidePanel/SidePanel'
import Spinner from '../../../../../ui/Spinner/Spinner'
import Button from '../../../../../ui/Button/Button'
import Icon from '../../../../../ui/Icon/Icon'
import Switch from '../../../../../ui/Switch/Switch'
import CatalogTaxonomyArchiveSection from '../CatalogTaxonomyArchiveSection/CatalogTaxonomyArchiveSection'
import CatalogTaxonomyStatusLine from '../CatalogTaxonomyStatusLine'
import { computeEffectiveState } from '../catalogTaxonomyEffectiveState'
import {
  partitionSiblingLists,
  reorderNonArchivedSiblings,
  subscribeCatalogTaxonomySiblings,
} from '../catalogTaxonomyApi'
import styles from './CatalogTaxonomyNodeList.module.scss'

function clearBrowserTextSelection() {
  const selection = window.getSelection && window.getSelection()
  if (selection && selection.removeAllRanges) {
    selection.removeAllRanges()
  }
}

function getItemClassName(node, selectedId, ancestors) {
  const { effectiveArchived, effectiveActive } = computeEffectiveState(node, ancestors)
  return [
    styles.item,
    `${selectedId}` === `${node.id}` ? styles.itemSelected : '',
    !effectiveActive || effectiveArchived ? styles.itemInactive : '',
  ]
    .filter(Boolean)
    .join(' ')
}

function formatProductCountDisplay(counts, status) {
  if (status === 'loading' || status === 'idle') return '…'
  if (status === 'error') return '—'
  if (!counts || typeof counts.total !== 'number') return '0'
  return String(counts.total)
}

function formatProductCountTooltip(counts, status) {
  if (status === 'loading' || status === 'idle') return 'Завантаження…'
  if (status === 'error') return 'Не вдалося завантажити кількість'
  const total = counts?.total ?? 0
  const active = counts?.active ?? 0
  const inactive = counts?.inactive ?? 0
  return [
    `Унікальних товарів у гілці: ${total}`,
    `Активних: ${active}`,
    `Неактивних: ${inactive}`,
  ].join('\n')
}

function NodeItemContent({ node, ancestors, productCountsByNodeId, productCountsStatus }) {
  const counts = productCountsByNodeId?.[node.id]
  const display = formatProductCountDisplay(counts, productCountsStatus)
  const tooltip = formatProductCountTooltip(counts, productCountsStatus)

  return (
    <div className={styles.itemBody}>
      <div className={styles.itemTitleRow}>
        <p className={styles.itemTitle}>{node.name || '—'}</p>
        <span className={styles.itemCount} title={tooltip}>
          {display}
        </span>
      </div>
      <CatalogTaxonomyStatusLine node={node} ancestors={ancestors} />
    </div>
  )
}

const DragHandle = SortableHandle(() => (
  <span
    className={styles.dragHandle}
    role="button"
    tabIndex={-1}
    aria-label="Змінити порядок"
    onClick={(e) => e.stopPropagation()}
  >
    <Icon name="bars" weight="solid" size={11} />
  </span>
))

const SortableNodeItem = SortableElement(
  ({ node, selectedId, onSelect, ancestors, productCountsByNodeId, productCountsStatus }) => (
    <div
      className={`${getItemClassName(node, selectedId, ancestors)} ${styles.itemReorderable}`}
      onClick={() => onSelect(node.id)}
    >
      <DragHandle />
      <NodeItemContent
        node={node}
        ancestors={ancestors}
        productCountsByNodeId={productCountsByNodeId}
        productCountsStatus={productCountsStatus}
      />
    </div>
  )
)

const SortableNodeList = SortableContainer(
  ({ nodes, selectedId, onSelect, ancestors, productCountsByNodeId, productCountsStatus }) => (
    <div className={styles.list}>
      {nodes.map((node, index) => (
        <SortableNodeItem
          key={node.id}
          index={index}
          node={node}
          selectedId={selectedId}
          onSelect={onSelect}
          ancestors={ancestors}
          productCountsByNodeId={productCountsByNodeId}
          productCountsStatus={productCountsStatus}
        />
      ))}
    </div>
  )
)

function StaticNodeList({
  nodes,
  selectedId,
  onSelect,
  ancestors,
  productCountsByNodeId,
  productCountsStatus,
}) {
  return (
    <div className={styles.list}>
      {nodes.map((node) => (
        <div
          key={node.id}
          className={getItemClassName(node, selectedId, ancestors)}
          onClick={() => onSelect(node.id)}
        >
          <NodeItemContent
            node={node}
            ancestors={ancestors}
            productCountsByNodeId={productCountsByNodeId}
            productCountsStatus={productCountsStatus}
          />
        </div>
      ))}
    </div>
  )
}

function CatalogTaxonomyNodeList({
  title,
  parentId,
  parentReady = true,
  selectedId = '',
  ancestors = [],
  onSelect,
  onCreate,
  emptyText = 'Немає елементів',
  parentHint = 'Оберіть батьківський рівень',
  createDisabledReason = null,
  productCountsByNodeId = {},
  productCountsStatus = 'idle',
}) {
  const [nodes, setNodes] = useState(null)
  const [error, setError] = useState(null)
  const [reorderError, setReorderError] = useState(null)
  const [listKey, setListKey] = useState(0)
  const [isReorderMode, setIsReorderMode] = useState(false)

  useEffect(() => {
    if (!parentReady) {
      setNodes([])
      setError(null)
      setReorderError(null)
      setIsReorderMode(false)
      return undefined
    }

    setNodes(null)
    setError(null)
    setReorderError(null)
    setIsReorderMode(false)

    return subscribeCatalogTaxonomySiblings(
      parentId,
      (data) => setNodes(data),
      (err) => {
        console.error(err)
        setError(err)
        setNodes([])
      }
    )
  }, [parentId, parentReady])

  const { nonArchived, archived } = useMemo(
    () => (isNull(nodes) ? { nonArchived: [], archived: [] } : partitionSiblingLists(nodes)),
    [nodes]
  )

  const onSortStart = () => {
    clearBrowserTextSelection()
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    clearBrowserTextSelection()
    if (oldIndex === newIndex) return

    const previousNodes = nodes
    const next = arrayMove(nonArchived, oldIndex, newIndex).map((node, index) => ({
      ...node,
      sortOrder: index,
    }))

    setReorderError(null)
    setNodes((prev) => {
      if (!prev) return prev
      const { archived: archivedNodes } = partitionSiblingLists(prev)
      return [...next, ...archivedNodes]
    })
    setListKey((key) => key + 1)

    reorderNonArchivedSiblings(next.map((node) => node.id)).catch((err) => {
      console.error(err)
      setNodes(previousNodes)
      setListKey((key) => key + 1)
      setReorderError('Не вдалося зберегти порядок. Спробуйте ще раз.')
    })
  }

  const showReorderToggle = parentReady && !isNull(nodes) && !error && !isEmpty(nonArchived)
  const createBlocked = Boolean(createDisabledReason)

  return (
    <SidePanel
      title={title}
      buttons={
        parentReady ? (
          <>
            {showReorderToggle ? (
              <div
                className={styles.reorderControl}
                title={isReorderMode ? 'Завершити зміну порядку' : 'Змінити порядок'}
              >
                <Icon
                  name="arrows-alt-v"
                  weight="solid"
                  size={12}
                  className={`${styles.reorderControlIcon} ${
                    isReorderMode ? styles.reorderControlIconActive : ''
                  }`}
                />
                <Switch
                  checked={isReorderMode}
                  onChange={setIsReorderMode}
                  ariaLabel={isReorderMode ? 'Завершити зміну порядку' : 'Змінити порядок'}
                  title={isReorderMode ? 'Завершити зміну порядку' : 'Змінити порядок'}
                />
              </div>
            ) : null}
            <Button
              className={createBlocked ? styles.createBlocked : undefined}
              theme="circle"
              fill="accent"
              icon="plus"
              title={createDisabledReason || undefined}
              onClick={() => {
                if (createBlocked) {
                  window.alert(createDisabledReason)
                  return
                }
                onCreate()
              }}
            />
          </>
        ) : null
      }
      content={
        <div className={styles.container}>
          {!parentReady ? (
            <p className={styles.empty}>{parentHint}</p>
          ) : isNull(nodes) ? (
            <Spinner type="popup" />
          ) : error ? (
            <p className={styles.empty}>Помилка завантаження. Перевірте консоль / Firestore indexes.</p>
          ) : (
            <>
              {reorderError ? <p className={styles.reorderError}>{reorderError}</p> : null}
              {isEmpty(nonArchived) ? (
                <p className={styles.empty}>{emptyText}</p>
              ) : isReorderMode ? (
                <SortableNodeList
                  key={`reorder-${listKey}`}
                  nodes={nonArchived}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  ancestors={ancestors}
                  productCountsByNodeId={productCountsByNodeId}
                  productCountsStatus={productCountsStatus}
                  axis="y"
                  lockAxis="y"
                  useDragHandle
                  helperClass={styles.dragHelper}
                  onSortStart={onSortStart}
                  onSortEnd={onSortEnd}
                />
              ) : (
                <StaticNodeList
                  nodes={nonArchived}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  ancestors={ancestors}
                  productCountsByNodeId={productCountsByNodeId}
                  productCountsStatus={productCountsStatus}
                />
              )}

              <CatalogTaxonomyArchiveSection
                nodes={archived}
                selectedId={selectedId}
                onSelect={onSelect}
                ancestors={ancestors}
              />
            </>
          )}
        </div>
      }
    />
  )
}

export default CatalogTaxonomyNodeList
